import React from 'react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import PinnedHeaderTable from './stickyTableHeader/PinnedHeaderTable';
import { RevelMetricAttribution } from '../../data/scorecardData';
import { MetricLabels } from '../../contexts/TenantContext';
import { MetricDefinition } from '../../types/prompt';
import { getScoreColor, getPercentageColor, formatAttribution } from '../../utils/metricUtils';
import type {
  RecentShiftColumn,
  RecentShiftRow,
  RecentShiftMetricValue,
} from '../../services/serverScorecardService';

export interface ShiftMetricsRow {
  date?: string;
  serverName?: string;
  managerName?: string;
  type: string;
  greetings: number;
  /**
   * feat-092: per-visit count (greeting-anchored `score_breakdown.visit_count`).
   * Optional because backend `v:3` breakdowns populate it but older `v:2`
   * breakdowns (and the un-recalc'd window between this deploy and the backend
   * recalc) omit it. The "Visits" column reads `visitCount ?? greetings`, so it
   * stays correct against both shapes. Backend remains the single source of this
   * count — React never recomputes it.
   */
  visitCount?: number;
  score: number;
  premiumMenu: number;
  itemMention: number;
  itemDescription: number;
  drinkPromotions: number;
  shiftId?: string;
  role?: string | null;
  revelAttribution?: {
    metric_1: RevelMetricAttribution;
    metric_2: RevelMetricAttribution;
    metric_3: RevelMetricAttribution;
    drink_promotion: RevelMetricAttribution;
    [key: string]: RevelMetricAttribution;
  };
  /** Dynamic metric values keyed by metric key (e.g. metric_1, metric_2, ...) */
  metricValues?: Record<string, number>;
}

interface ShiftMetricsTableProps {
  /** Legacy rows. Optional because the feat-091 Recent-Shifts mode (`recentShifts`)
   *  supplies its own row shape instead. */
  shifts?: ShiftMetricsRow[];
  showDate?: boolean;
  showServer?: boolean;
  showManager?: boolean;
  showShift?: boolean;
  showGreetings?: boolean;
  showMetrics?: boolean;
  onViewDetails?: (row: ShiftMetricsRow) => void;
  emptyMessage?: string;
  metricLabels?: MetricLabels;
  /**
   * When provided, renders dynamic metric columns from these definitions
   * instead of the hardcoded 3+1 columns. Each shift row should have
   * metricValues[metric.key] set.
   */
  dynamicMetrics?: MetricDefinition[];
  dynamicDrinkLabel?: string | null;
  /** When true, shows a loading placeholder for score cells */
  scoresLoading?: boolean;
  /**
   * feat-091 opt-in: when provided, renders the parity Recent-Shifts table —
   * columns in endpoint `display_order` with the ★ scoring-vs-reference header
   * treatment, Score before Greetings, plain neutral cells (no red-<80% pills),
   * and the per-shift Score-cell tooltip. Every value comes from the backend
   * response verbatim (AC#11). When ABSENT the legacy table above renders
   * unchanged, so the four other ShiftMetricsTable consumers are untouched (D-7).
   */
  recentShifts?: {
    metricColumns: RecentShiftColumn[];
    drinkColumn: RecentShiftColumn | null;
    rows: RecentShiftRow[];
    emptyMessage?: string;
  };
  /**
   * feat-297 opt-in: pin the column-header row beneath ScorecardNavigation while
   * the page scrolls (see stickyTableHeader/PinnedHeaderTable). Passed ONLY by
   * ManagerScorecard and StoreScorecard. Off by default so the shift-detail
   * consumers render the legacy markup byte-for-byte; the `recentShifts` branch
   * never pins. The caller's card must be `overflow-clip`, not `overflow-hidden`.
   */
  pinHeader?: boolean;
}

// Empty default. The tenant's prompt config / metric_labels drive the column
// headers; if a component forgets to pass `metricLabels`, the table renders
// no metric columns rather than ghost SAJJ ones.
const DEFAULT_LABELS: MetricLabels = {};

/**
 * Get the percentage value for a metric key from a ShiftMetricsRow.
 * Checks metricValues first (dynamic), then falls back to legacy fields.
 */
function getMetricPercent(shift: ShiftMetricsRow, key: string): number {
  if (shift.metricValues && key in shift.metricValues) {
    return shift.metricValues[key];
  }
  // Legacy field mapping
  if (key === 'metric_1') return shift.premiumMenu;
  if (key === 'metric_2') return shift.itemMention;
  if (key === 'metric_3') return shift.itemDescription;
  if (key === 'drink_promotion') return shift.drinkPromotions;
  return 0;
}

const ShiftMetricsTable: React.FC<ShiftMetricsTableProps> = ({
  shifts = [],
  showDate = false,
  showServer = false,
  showManager = false,
  showShift = true,
  showGreetings = true,
  showMetrics = true,
  onViewDetails,
  emptyMessage = 'No shift data available for the selected month.',
  metricLabels = DEFAULT_LABELS,
  dynamicMetrics,
  dynamicDrinkLabel,
  scoresLoading = false,
  recentShifts,
  pinHeader = false,
}) => {
  // feat-091 (Slice B): the parity Recent-Shifts table is a self-contained render
  // path. Gating on `recentShifts` keeps the legacy table below byte-identical for
  // the four other consumers (StoreScorecard, ManagerScorecard, Store/Manager
  // ShiftDetails), per ADR 0001 D-7.
  if (recentShifts) {
    return <RecentShiftsTable {...recentShifts} />;
  }

  // Determine columns: dynamic if provided, otherwise hardcoded 3+1
  const usesDynamic = !!dynamicMetrics && dynamicMetrics.length > 0;

  // Non-dynamic path: render one column per key in metricLabels (excluding
  // the drink column, which has its own slot below). This lets tenants whose
  // metric_labels use real service_criteria keys (e.g. add_on_upsell,
  // suggestion_box, recommendation_box) drive the Scorecard columns directly,
  // instead of being locked into the legacy metric_1/2/3 trio.
  const metricColumns: { key: string; label: string }[] = usesDynamic
    ? dynamicMetrics!.map((m) => ({ key: m.key, label: m.label }))
    : Object.entries(metricLabels)
        .filter(([key]) => key !== 'drink_promotion')
        .map(([key, label]) => ({ key, label }));

  const drinkLabel = dynamicDrinkLabel !== undefined
    ? dynamicDrinkLabel
    : metricLabels.drink_promotion;

  const headerClass = "px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider";

  return (
    <PinnedHeaderTable
      enabled={pinHeader}
      scrollerClassName="overflow-x-auto"
      tableClassName="min-w-full"
      thead={
        <thead className="bg-gradient-to-r from-coral via-gold to-coral">
          <tr>
            {showDate && (
              <th className={headerClass}>Date</th>
            )}
            {showServer && (
              <th className={headerClass}>Server</th>
            )}
            {showShift && (
              <th className={headerClass}>Shift</th>
            )}
            {showGreetings && (
              <th className={headerClass}>Visits</th>
            )}
            <th className={headerClass}>Score</th>
            {showMetrics && metricColumns.map((col) => (
              <th key={col.key} className={headerClass}>
                {col.label}
              </th>
            ))}
            {showMetrics && drinkLabel && (
              <th className={headerClass}>{drinkLabel}</th>
            )}
            {showManager && (
              <th className={headerClass}>Manager</th>
            )}
            {onViewDetails && (
              <th className={headerClass}>Details</th>
            )}
          </tr>
        </thead>
      }
    >
        <tbody className="bg-white divide-y divide-softBorder">
          {shifts.length > 0 ? (
            shifts.map((shift, index) => (
              <tr key={index} className="hover:bg-cream-dark/40 transition-colors">
                {showDate && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink">
                    {shift.date}
                  </td>
                )}
                {showServer && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-ink">
                    {shift.serverName || '\u2014'}
                  </td>
                )}
                {showShift && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      shift.type.toLowerCase() === 'lunch'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {shift.type.charAt(0).toUpperCase() + shift.type.slice(1).toLowerCase()}
                    </span>
                  </td>
                )}
                {showGreetings && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink">
                    {/* feat-092: per-visit count. Falls back to raw greeting rows
                        when the backend hasn't populated `visit_count` yet (v:2
                        breakdown / pre-recalc window). */}
                    {shift.visitCount ?? shift.greetings}
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-ink">
                  {scoresLoading ? (
                    <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-400">...</span>
                  ) : (
                    <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${getScoreColor(shift.score)}`}>
                      {shift.score}
                    </span>
                  )}
                </td>
                {showMetrics && metricColumns.map((col) => {
                  const pct = getMetricPercent(shift, col.key);
                  const attr = shift.revelAttribution?.[col.key];
                  return (
                    <td key={col.key} className="px-6 py-4 text-sm text-ink">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPercentageColor(pct)}`}>
                        {pct}%
                      </span>
                      {formatAttribution(attr) && (
                        <div className="text-xs text-inkMuted mt-1">
                          {formatAttribution(attr)}
                        </div>
                      )}
                    </td>
                  );
                })}
                {showMetrics && drinkLabel && (
                  <td className="px-6 py-4 text-sm text-ink">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPercentageColor(getMetricPercent(shift, 'drink_promotion'))}`}>
                      {getMetricPercent(shift, 'drink_promotion')}%
                    </span>
                    {formatAttribution(shift.revelAttribution?.drink_promotion) && (
                      <div className="text-xs text-inkMuted mt-1">
                        {formatAttribution(shift.revelAttribution?.drink_promotion)}
                      </div>
                    )}
                  </td>
                )}
                {showManager && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink">
                    <span className="font-semibold text-ink">
                      {shift.managerName || '\u2014'}
                    </span>
                  </td>
                )}
                {onViewDetails && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => onViewDetails(shift)}
                      className="text-coral hover:text-coral/80 font-semibold text-sm transition-colors"
                    >
                      View Details
                    </button>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={99} className="px-6 py-4 text-center text-sm text-inkMuted">
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
    </PinnedHeaderTable>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// feat-091 — Recent Shifts parity table (Slice B). Self-contained render path,
// reached only when `recentShifts` is passed (legacy path above is untouched).
//
// Columns render in the endpoint's `display_order`; scoring columns get a ★ +
// legend (design Option A); cells are plain neutral numbers (design O1 — no
// red-<80% pills); the Score cell carries the per-shift breakdown tooltip. Every
// value is rendered verbatim from the backend response (AC#11 — zero React math).
// ════════════════════════════════════════════════════════════════════════════

const recentHeaderClass =
  'px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider';

// feat-234 — the Score header. Spelled out rather than `${recentHeaderClass}
// text-right` so it does not depend on Tailwind emitting `.text-right` after
// `.text-left`: both utilities have equal specificity, so a class string carrying
// BOTH would be decided by stylesheet order, not by the order written here.
const recentScoreHeaderClass =
  'px-4 py-4 text-right text-xs font-bold text-white uppercase tracking-wider';

const ScoringStar: React.FC<{ show: boolean }> = ({ show }) =>
  show ? (
    <span aria-hidden="true" className="text-white">
      ★{' '}
    </span>
  ) : null;

const ShiftTypePill: React.FC<{ label: string }> = ({ label }) => {
  const isLunch = (label || '').toLowerCase() === 'lunch';
  return (
    <span
      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
        isLunch ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
      }`}
    >
      {label ? label.charAt(0).toUpperCase() + label.slice(1).toLowerCase() : '—'}
    </span>
  );
};

// A plain integer cell, or a muted em-dash when the value is null (Greetings on a
// non-scored row). Renders the backend value verbatim — no math.
const PlainValue: React.FC<{ value: number | null }> = ({ value }) =>
  value === null || value === undefined ? (
    <span className="text-inkMuted/60">{'—'}</span>
  ) : (
    <span className="tabular-nums">{value}</span>
  );

// A per-metric cell. "—" (a gap, not a 0) when the metric is absent from this
// shift's frozen snapshot (`present:false` — e.g. a column added after the shift)
// or the row is non-scored (empty `metrics`).
//
// feat-234: ALWAYS the raw `count`. The per-visit `percent` is still on
// RecentShiftMetricValue and still computed by the backend (deliberate — it is
// cheap to switch percentages back on for a fixed-station tenant), it is simply
// no longer rendered: dividing an upsell count by ALL of a rotating server's
// visits maligns servers who were rarely at that station. Rendered verbatim from
// the backend — no recompute (feedback_metrics_single_source).
const MetricCountCell: React.FC<{
  value: RecentShiftMetricValue | null | undefined;
}> = ({ value }) => {
  if (!value || !value.present) {
    return <span className="text-inkMuted/60">{'—'}</span>;
  }
  if (value.count === null || value.count === undefined) {
    return <span className="text-inkMuted/60">{'—'}</span>;
  }
  return <span className="tabular-nums">{value.count}</span>;
};

// The Score-cell tooltip body. Scored → breakdown table + summed formula +
// frozen prompt-version note (all from the backend's `score_tooltip`). Non-scored
// → the verbatim status message (ADR §D-6b). React does ZERO math here.
const ScoreTooltipBody: React.FC<{ row: RecentShiftRow }> = ({ row }) => {
  if (!row.scored || !row.score_tooltip) {
    const msg =
      row.score_status === 'version_unknown'
        ? 'Prompt version unknown for this shift; score cannot be shown.'
        : 'Score pending — this shift has not been scored yet.';
    return (
      <div className="rounded-xl overflow-hidden text-left">
        <div className="bg-cream-dark px-4 py-2 text-xs font-semibold text-ink">Score unavailable</div>
        <div className="px-4 py-3 text-sm text-ink max-w-[280px]">{msg}</div>
      </div>
    );
  }
  const t = row.score_tooltip;
  return (
    <div className="rounded-xl overflow-hidden text-left">
      <div className="bg-gradient-to-r from-coral to-gold px-4 py-2.5">
        <div className="text-sm font-bold text-white">How this score was calculated</div>
        <div className="text-xs text-white/85">
          {row.shift_label} · {row.shift_date}
        </div>
      </div>
      <div className="px-4 py-3 text-ink">
        {t.scoring_metrics.length > 0 ? (
          <table className="w-full text-xs mb-2">
            <thead>
              <tr className="text-inkMuted text-left">
                <th className="font-semibold pr-3 pb-1">Scoring metric</th>
                <th className="font-semibold px-2 pb-1 text-right">Attempts</th>
                <th className="font-semibold pl-2 pb-1 text-right">% of visits</th>
              </tr>
            </thead>
            <tbody>
              {t.scoring_metrics.map((m, i) => (
                <tr key={i} className="border-t border-softBorder">
                  <td className="pr-3 py-1">{m.label}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{m.count}</td>
                  <td className="pl-2 py-1 text-right tabular-nums">{m.percent}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-xs text-inkMuted mb-2">No scoring-metric attempts in this shift.</div>
        )}
        {/* Summed formula with values plugged in — backend-produced string (AC#5). */}
        <div className="text-xs font-mono text-ink bg-cream rounded-md px-2 py-1.5 mb-2">
          {t.formula_human}
        </div>
        {/* Frozen prompt-version note (AC#5). */}
        <div className="text-[11px] text-inkMuted">{t.version_note}</div>
      </div>
    </div>
  );
};

const RecentShiftsTable: React.FC<{
  metricColumns: RecentShiftColumn[];
  drinkColumn: RecentShiftColumn | null;
  rows: RecentShiftRow[];
  emptyMessage?: string;
}> = ({
  metricColumns,
  drinkColumn,
  rows,
  emptyMessage = 'No shift data available for the selected month.',
}) => {
  // Stable per-instance tooltip id (avoids collisions if two tables ever mount).
  const tooltipId = React.useId();
  const rowById = React.useMemo(() => {
    const m = new Map<string, RecentShiftRow>();
    rows.forEach((r) => m.set(r.shift_id, r));
    return m;
  }, [rows]);

  // Date, Shift, Score, Greetings, <metrics…>, [Drink].
  const colCount = 4 + metricColumns.length + (drinkColumn ? 1 : 0);
  const wideHeader = `${recentHeaderClass} min-w-[96px] max-w-[170px] whitespace-normal`;

  return (
    <>
      {/* Only the metric grid scrolls horizontally inside its card; the page
          never h-scrolls (design O3, accepted per spec "scroll likely stays"). */}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gradient-to-r from-coral via-gold to-coral align-bottom">
            <tr>
              <th className={recentHeaderClass}>Date</th>
              <th className={recentHeaderClass}>Shift</th>
              {/* Score precedes Visits (parity with the scoring story).
                  feat-234: right-aligned. The Score now carries a `%` suffix, and
                  `tabular-nums` cannot align it — neither Fredoka (font-display,
                  used by the Score cell) nor Nunito ships a `tnum` GSUB feature,
                  so that class has always been inert here, and Fredoka's "1" is
                  34% narrower than its "2". Left-aligned, the repeated % glyph
                  lands at a different x on every row and reads as broken. */}
              <th className={recentScoreHeaderClass}>Score</th>
              <th className={recentHeaderClass}>Visits</th>
              {metricColumns.map((col) => (
                <th key={col.key} className={wideHeader}>
                  <ScoringStar show={col.is_scoring} />
                  {col.label}
                </th>
              ))}
              {drinkColumn && (
                <th className={wideHeader}>
                  <ScoringStar show={drinkColumn.is_scoring} />
                  {drinkColumn.label}{' '}
                  <span className="normal-case font-normal text-white/80">(drink)</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-softBorder">
            {rows.length > 0 ? (
              rows.map((shift) => (
                <tr key={shift.shift_id} className="hover:bg-cream-dark/40 transition-colors">
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-ink">{shift.shift_date}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-ink">
                    <ShiftTypePill label={shift.shift_label} />
                  </td>
                  {/* Score — plain neutral value (O1); tooltip on hover (AC#5/#7).
                      The ONLY cell with a tooltip (AC#6). feat-234: right-aligned
                      so the `%` suffix is a fixed anchor down the column (see the
                      header comment). */}
                  <td className="px-4 py-4 text-right whitespace-nowrap text-sm">
                    <span
                      data-tooltip-id={tooltipId}
                      data-tooltip-content={shift.shift_id}
                      className={`font-display text-base tabular-nums cursor-help underline decoration-dotted underline-offset-4 ${
                        shift.scored ? 'font-semibold text-ink' : 'text-inkMuted/60'
                      }`}
                    >
                      {shift.score_display}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-ink">
                    {/* feat-093 Slice B: Visits = backend `visit_count`
                        (greeting-anchored, feat-092) with the v:2 `greetings`
                        fallback. NULLISH (??) so a real 0 shows 0 and a non-scored
                        row (null greetings) shows "—". Backend single-sources it. */}
                    <PlainValue value={shift.visit_count ?? shift.greetings} />
                  </td>
                  {metricColumns.map((col) => (
                    <td key={col.key} className="px-4 py-4 whitespace-nowrap text-sm text-ink">
                      <MetricCountCell value={shift.metrics[col.key]} />
                    </td>
                  ))}
                  {drinkColumn && (
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-ink">
                      <MetricCountCell value={shift.drink} />
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={colCount} className="px-6 py-4 text-center text-sm text-inkMuted">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Legend — always shown whenever the ★ treatment is shown (design O4),
          i.e. whenever there is at least one metric/drink column to mark. */}
      {(metricColumns.length > 0 || drinkColumn) && (
        <div className="px-4 py-3 border-t border-softBorder text-xs text-inkMuted">
          <span className="text-gold font-bold" aria-hidden="true">
            ★
          </span>{' '}
          = counts toward the score (upsell-attempt rate). Other columns are reference
          service metrics and do not affect the score.
        </div>
      )}

      {/* One light-card tooltip instance for every Score cell in this table. */}
      <Tooltip
        id={tooltipId}
        place="top"
        opacity={1}
        clickable
        style={{
          padding: 0,
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          zIndex: 60,
          maxWidth: '340px',
        }}
        render={({ content }) => {
          const row = content ? rowById.get(content) : undefined;
          if (!row) return null;
          return <ScoreTooltipBody row={row} />;
        }}
      />
    </>
  );
};

export default ShiftMetricsTable;
